import { defineStore } from 'pinia'
import jwtInterceptor from '@/plugins/jwtInterceptor'
import * as Sentry from '@sentry/vue'
import type { ReleaseNoteItemApi } from '@/interfaces/api/releaseNoteItemApi'
import { useUserStore } from '@/stores/user'

export const useReleaseNoteStore = defineStore('releaseNote', {
  state: (): {
    unseenItems: ReleaseNoteItemApi[]
    showCarousel: boolean
    isLoading: boolean
    error: string | null
    previewMode: boolean
  } => ({
    unseenItems: [],
    showCarousel: false,
    isLoading: false,
    error: null,
    previewMode: false,
  }),

  actions: {
    async fetchUnseen(): Promise<void> {
      this.isLoading = true
      this.error = null
      try {
        const response = await jwtInterceptor.get<ReleaseNoteItemApi[]>('v1/release-notes/unseen')
        this.unseenItems = response?.data ?? []
        this.showCarousel = this.unseenItems.length > 0
      } catch (error) {
        this.error = 'Failed to load release notes'
        Sentry.captureException(error, { tags: { store: 'releaseNote', action: 'fetchUnseen' } })
      } finally {
        this.isLoading = false
      }
    },

    async markSeen(): Promise<void> {
      const version = import.meta.env.VITE_APP_VERSION as string | undefined
      try {
        await jwtInterceptor.post('v1/release-notes/mark-seen', { version: version ?? null })
        const userStore = useUserStore()
        if (userStore.user && version) {
          userStore.user.lastSeenVersion = version
        }
      } catch (error) {
        Sentry.captureException(error, { tags: { store: 'releaseNote', action: 'markSeen' } })
      } finally {
        this.showCarousel = false
      }
    },

    closeWithoutMarkSeen(): void {
      this.showCarousel = false
    },

    openPreview(items: ReleaseNoteItemApi[]): void {
      this.unseenItems = items
      this.showCarousel = true
      this.previewMode = true
    },

    exitPreview(): void {
      this.previewMode = false
      this.showCarousel = false
      this.unseenItems = []
    },
  },
})
